// TypeScript is ignoring this file. If you are editing this file you must fix the type issues.

import React, {
  Dispatch, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { STYLES } from '../constants';
import Icon from '../../../generic-components/Icon';
import lifeExpensesIcon from '../../../assets/icons/svg/colored-icons/ic-colored-categorization-personal.svg';
import workExpenseIcon from '../../../assets/icons/svg/colored-icons/ic-colored-categorization-business.svg';
import { AmountInput } from './SplitTransaction/AmountInput';

interface IProps {
    shouldDisplaySplittingResult?: any,
    businessPercent: number,
    setBusinessPercent: Dispatch<React.SetStateAction<number>>,
    transactionAmount: number,
}

const getAmountInputCalculation = (transactionAmount: number, businessPercent: number, isBusinessAmount: boolean) => (isBusinessAmount ? transactionAmount * businessPercent : transactionAmount * (1 - businessPercent));
const getCalculatedBusinessAmount = (transactionAmount: number, businessPercent: number) => businessPercent * transactionAmount;
const getCalculatedPersonalAmount = (transactionAmount: number, calcBusinessAmount: number) => transactionAmount - calcBusinessAmount;
export const SplitTransactionRangeSlider = ({
  businessPercent, setBusinessPercent, shouldDisplaySplittingResult, transactionAmount,
} : IProps) => {
  const [rangePercent, setRangePercent] = useState<number>(businessPercent);
  const [personalAmount, setPersonalAmount] = useState<number>(getAmountInputCalculation(transactionAmount, businessPercent, false));
  const [businessAmount, setBusinessAmount] = useState<number>(getAmountInputCalculation(transactionAmount, businessPercent, true));
  const [step, setStep] = useState<number>(0);
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement | null>(null);
  const businessPercentDisplay = Math.round(rangePercent * 100);
  const personalPercentDisplay = Math.round(100 - (rangePercent * 100));

  useEffect(() => {
    const rangeLinePadding = 16;
    const calcStep = (ref.current!.offsetWidth - rangeLinePadding) / parseFloat(ref.current!.max);
    setStep(calcStep);
  }, []);

  useEffect(() => {
    setBusinessPercent(rangePercent);
  }, [rangePercent]);

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const businessPercentFromRange = parseFloat(e.target.value);
    const calculatedBusinessAmount = getCalculatedBusinessAmount(transactionAmount, businessPercentFromRange);
    setRangePercent(businessPercentFromRange);
    setBusinessAmount(calculatedBusinessAmount);
    setPersonalAmount(getCalculatedPersonalAmount(transactionAmount, calculatedBusinessAmount));
  };

  const getBusinessPercentCalcByAmount = (inputAmount: number, isBusinessInputAmount: boolean): number => {
    let businessPercentCalc;
    if (isBusinessInputAmount) businessPercentCalc = inputAmount / transactionAmount;
    else businessPercentCalc = (transactionAmount - inputAmount) / transactionAmount;

    return businessPercentCalc;
  };

  const isAmountValid = (amount: number) => amount >= 0 && amount <= transactionAmount;
  const handleOnAmountChange = (amount: number, isManualBusinessAmount: boolean) => {
    if (isAmountValid(amount)) {
      const calcBusinessPercent = getBusinessPercentCalcByAmount(amount, isManualBusinessAmount);
      setRangePercent(calcBusinessPercent);
      const calcBusinessAmount = getCalculatedBusinessAmount(transactionAmount, calcBusinessPercent);
      const calcPersonalAmount = getCalculatedPersonalAmount(transactionAmount, calcBusinessAmount);
      if (isManualBusinessAmount) setPersonalAmount(calcPersonalAmount);
      else setBusinessAmount(calcBusinessAmount);
    }
  };
  return (
    <div>
      <label htmlFor='customRange1' className={STYLES.RANGE_PICKER.RANGE_PICKER_LABEL}>
        {t('rangePickerLabel')}
      </label>
      <div
        className={STYLES.RANGE_PICKER.TOOLTIP_WRAPPER}
        style={{
          transform: `translateX(${parseFloat(rangePercent.toFixed(2)) * step - 15}px)`,
        }}
      >
        <p className={STYLES.RANGE_PICKER.TOOLTIP_TEXT} data-testid='categorization-split-personal-percent'>{personalPercentDisplay}</p>
        <div className={STYLES.RANGE_PICKER.TOOLTIP_DIVIDER} />
        <p className={STYLES.RANGE_PICKER.TOOLTIP_TEXT} data-testid='categorization-split-business-percent'>{businessPercentDisplay}</p>
      </div>
      <div className='w-[280px] mx-auto my-0'>
        <input
          value={rangePercent}
          min='0'
          max='1'
          step='0.0001'
          id='splitTransactionRange'
          className='w-full h-[6px] bg-[#DBF0F7] appearance-none rounded-md'
          type='range'
          onChange={handleRangeChange}
          ref={ref}
        />
      </div>
      {shouldDisplaySplittingResult && (
        <div className={STYLES.RANGE_PICKER.SPLITTING_RESULT_WRAPPER}>
          <div className={STYLES.RANGE_PICKER.SPLITTING_RESULT_BOX}>
            <Icon iconId='lifeExpensesIcon' className={STYLES.RANGE_PICKER.ICON_PERSONAL} src={lifeExpensesIcon} />
            <AmountInput amount={personalAmount} maxAmount={transactionAmount} name='personalAmount' onAmountChange={handleOnAmountChange} />
          </div>
          <div className={STYLES.RANGE_PICKER.SPLITTING_RESULT_BOX}>
            <Icon iconId='workExpenseIcon' className={STYLES.RANGE_PICKER.ICON_BUSINESS} src={workExpenseIcon} />
            <AmountInput amount={businessAmount} maxAmount={transactionAmount} name='businessAmount' onAmountChange={handleOnAmountChange} />
          </div>
        </div>
      )}
    </div>
  );
};
